import React, { useEffect, useRef } from 'react';

/** Small accessible modal: Escape or Cancel closes, focus starts on the safe choice and stays inside. */
export default function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  onConfirm, onCancel, danger = false, busy = false, confirmDisabled = false, testid = 'confirm-dialog' }) {
  const panel = useRef(null);
  const cancelRef = useRef(null);
  // Callers pass a fresh onCancel every render. If the effect below depended on it, every keystroke in a text
  // field would re-run it and pull focus back to the button. Read it through a ref so it runs once per open.
  const cancelHandler = useRef(onCancel);
  cancelHandler.current = onCancel;

  useEffect(() => {
    if (!open) return undefined;
    // A dialog that asks for text starts in the text field; otherwise on the safe choice.
    (panel.current?.querySelector('textarea, input') || cancelRef.current)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancelHandler.current?.(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const els = panel.current.querySelectorAll('button:not([disabled]), input, select, textarea, a[href]');
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink/40 p-0 sm:p-4" data-testid={`${testid}-backdrop`}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`} data-testid={testid}
        className="w-full sm:max-w-lg max-h-[90vh] overflow-auto bg-surface rounded-t-2xl sm:rounded-2xl shadow-ambient p-6">
        <h2 id={`${testid}-title`} className="font-display text-xl text-ink mb-3">{title}</h2>
        <div className="text-sm text-ink-muted space-y-3">{children}</div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button ref={cancelRef} type="button" onClick={onCancel} data-testid={`${testid}-cancel`}
            className="min-h-[48px] px-5 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">
            {cancelLabel}
          </button>
          <button type="button" onClick={onConfirm} disabled={busy || confirmDisabled} data-testid={`${testid}-confirm`}
            className={`min-h-[48px] px-5 rounded-xl text-white font-medium disabled:opacity-50 ${danger ? 'bg-danger hover:opacity-90' : 'bg-primary-500 hover:bg-primary-600'}`}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
